import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { MessageProps } from '../../../types/chat';
import MessageBubble from './MessageBubble';

const VideoMessage: React.FC<MessageProps> = React.memo(({ 
  message, 
  isCurrentUser,
  onImagePress,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  currentUserId,
  currentUserType
}) => {
  const [error, setError] = useState<string | null>(null);
  const [videoDimensions, setVideoDimensions] = useState<{width: number, height: number} | null>(null);

  const handleReactionPress = (emoji: string) => {
    onReactionPress?.(message, emoji);
  };

  const handleDownload = useCallback(async () => {
    if (!message.videoUrl) return;
    
    try {
      const response = await fetch(message.videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `video-${message.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download video:', error);
    }
  }, [message.videoUrl, message.id]);

  const handleVideoError = useCallback(() => {
    setError('Failed to load video');
  }, []);

  const handleVideoLoaded = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    setVideoDimensions({
      width: video.videoWidth,
      height: video.videoHeight
    });
  }, []);

  if (!message.videoUrl) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        currentUserId={currentUserId}
      >
        <Box sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Video not available
        </Box>
      </MessageBubble>
    );
  }

  if (error) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        currentUserId={currentUserId}
      >
        <Box sx={{ 
          p: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          textAlign: 'center',
          color: 'text.secondary'
        }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Box>
      </MessageBubble>
    );
  }

  return (
    <MessageBubble 
      message={message} 
      isCurrentUser={isCurrentUser}
      onReactionPress={handleReactionPress}
      onReplyPress={onReplyPress}
      onDeletePress={onDeletePress}
      onImagePress={onImagePress}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
    >
      <Box sx={{ 
        position: 'relative', 
        maxWidth: 400, 
        width: '100%',
        mb: 0.1 // Extremely reduced bottom margin
      }}>
        <Box sx={{ 
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: 'black',
          position: 'relative',
          // Remove fixed dimensions to let video determine its natural size
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.1 // Extremely reduced margin for the video container
        }}>
          {/* Video element */}
          <video
            width="100%"
            height="auto"
            controls
            playsInline
            preload="metadata"
            onError={handleVideoError}
            onLoadedMetadata={handleVideoLoaded}
            style={{
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              backgroundColor: 'black'
            }}
          >
            <source src={message.videoUrl} type="video/mp4" />
            <source src={message.videoUrl} type="video/webm" />
            <source src={message.videoUrl} type="video/ogg" />
            Your browser does not support the video tag.
          </video>
          
          {/* Download button overlay */}
          <IconButton
            onClick={handleDownload}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.8)',
              }
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Video info */}
        <Box sx={{ 
          mt: 0.25, // Extremely reduced margin top
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {(videoDimensions || (message.videoWidth && message.videoHeight)) && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {videoDimensions 
                ? `${videoDimensions.width}×${videoDimensions.height}`
                : `${message.videoWidth}×${message.videoHeight}`
              }
            </Typography>
          )}
        </Box>
        
        {/* Optional message text with video */}
        {message.message && message.message.trim() && (
          <Box sx={{ mt: 0.25, fontSize: '14px', lineHeight: 1.4 }}> 
            {message.message}
          </Box>
        )}
      </Box>
    </MessageBubble>
  );
});

VideoMessage.displayName = 'VideoMessage';

export default VideoMessage;
